/**
 * CustomFormUpload Component
 *
 * Phase 6.6: User-Uploaded Forms
 *
 * User-facing component for uploading custom PDF forms.
 * Simplified upload flow with drag-and-drop support.
 */

import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  FileText,
  Upload,
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';

import {
  useUploadCustomForm,
  useTriggerFormAnalysis,
  type CreateCustomFormInput,
} from '@/hooks/useCustomForms';

// =============================================================================
// TYPES
// =============================================================================

interface CustomFormUploadProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (formId: string) => void;
  jurisdictions?: Array<{ code: string; name: string }>;
  permitTypes?: Array<{ value: string; label: string }>;
}

type UploadStep = 'select' | 'details' | 'uploading' | 'analyzing' | 'complete';

// =============================================================================
// DEFAULT OPTIONS
// =============================================================================

const DEFAULT_PERMIT_TYPES = [
  { value: 'electrical', label: 'Electrical' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'mechanical', label: 'Mechanical / HVAC' },
  { value: 'roofing', label: 'Roofing' },
  { value: 'building', label: 'Building' },
  { value: 'general', label: 'General / Other' },
];

// =============================================================================
// COMPONENT
// =============================================================================

export function CustomFormUpload({
  open,
  onOpenChange,
  onSuccess,
  jurisdictions = [],
  permitTypes = DEFAULT_PERMIT_TYPES,
}: CustomFormUploadProps) {
  // State
  const [step, setStep] = useState<UploadStep>('select');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formName, setFormName] = useState('');
  const [description, setDescription] = useState('');
  const [jurisdictionCode, setJurisdictionCode] = useState<string>('');
  const [permitType, setPermitType] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [createdFormId, setCreatedFormId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Mutations
  const uploadMutation = useUploadCustomForm();
  const analyzeMutation = useTriggerFormAnalysis();

  // File drop handler
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];

      // Validate file
      if (file.type !== 'application/pdf') {
        setError('Please upload a PDF file');
        return;
      }

      if (file.size > 25 * 1024 * 1024) { // 25MB limit
        setError('File size must be less than 25MB');
        return;
      }

      setSelectedFile(file);
      setFormName(file.name.replace('.pdf', '').replace(/_/g, ' '));
      setError(null);
      setStep('details');
    }
  }, []);

  // Dropzone config
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    multiple: false,
  });

  // Reset state
  const resetState = () => {
    setStep('select');
    setSelectedFile(null);
    setFormName('');
    setDescription('');
    setJurisdictionCode('');
    setPermitType('');
    setUploadProgress(0);
    setCreatedFormId(null);
    setError(null);
  };

  // Handle dialog close
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetState();
    }
    onOpenChange(newOpen);
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile || !formName.trim()) return;

    setStep('uploading');
    setError(null);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      // Upload the form
      const input: CreateCustomFormInput = {
        form_name: formName.trim(),
        description: description.trim() || undefined,
        jurisdiction_code: jurisdictionCode || undefined,
        permit_type: permitType || undefined,
        pdf_file: selectedFile,
      };

      const result = await uploadMutation.mutateAsync(input);
      clearInterval(progressInterval);
      setUploadProgress(100);
      setCreatedFormId(result.id);

      // Start AI analysis
      setStep('analyzing');
      await analyzeMutation.mutateAsync(result.id);

      // Complete!
      setStep('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload form');
      setStep('details');
    }
  };

  // Handle completion
  const handleComplete = () => {
    if (createdFormId) {
      onSuccess?.(createdFormId);
    }
    handleOpenChange(false);
  };

  // Render file selection step
  const renderSelectStep = () => (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={cn(
          'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
          isDragActive
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-primary/50'
        )}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
            <Upload className="h-6 w-6 text-muted-foreground" />
          </div>
          {isDragActive ? (
            <p className="text-lg font-medium text-primary">Drop your PDF here</p>
          ) : (
            <>
              <p className="text-lg font-medium">
                Drag & drop your PDF form here
              </p>
              <p className="text-sm text-muted-foreground">
                or click to browse files
              </p>
            </>
          )}
          <Badge variant="secondary" className="text-xs">
            PDF files only, max 25MB
          </Badge>
        </div>
      </div>

      <Alert>
        <Sparkles className="h-4 w-4" />
        <AlertDescription>
          Our AI will analyze your form to identify fields and create smart auto-fill mappings.
          You'll be able to review and correct any mappings before using the form.
        </AlertDescription>
      </Alert>
    </div>
  );

  // Render details step
  const renderDetailsStep = () => (
    <div className="space-y-4">
      {/* Selected file preview */}
      {selectedFile && (
        <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
          <FileText className="h-8 w-8 text-primary" />
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{selectedFile.name}</p>
            <p className="text-sm text-muted-foreground">
              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedFile(null);
              setStep('select');
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Form name */}
      <div className="space-y-2">
        <Label htmlFor="form-name">Form Name *</Label>
        <Input
          id="form-name"
          value={formName}
          onChange={(e) => setFormName(e.target.value)}
          placeholder="e.g., Electrical Permit Application"
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">Description (optional)</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What is this form used for?"
          rows={2}
        />
      </div>

      {/* Jurisdiction */}
      {jurisdictions.length > 0 && (
        <div className="space-y-2">
          <Label htmlFor="jurisdiction">Jurisdiction (optional)</Label>
          <Select value={jurisdictionCode} onValueChange={setJurisdictionCode}>
            <SelectTrigger id="jurisdiction">
              <SelectValue placeholder="Select jurisdiction..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">Any / General</SelectItem>
              {jurisdictions.map((j) => (
                <SelectItem key={j.code} value={j.code}>
                  {j.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Permit type */}
      <div className="space-y-2">
        <Label htmlFor="permit-type">Permit Type (optional)</Label>
        <Select value={permitType} onValueChange={setPermitType}>
          <SelectTrigger id="permit-type">
            <SelectValue placeholder="Select permit type..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">Any / General</SelectItem>
            {permitTypes.map((pt) => (
              <SelectItem key={pt.value} value={pt.value}>
                {pt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  // Render uploading step
  const renderUploadingStep = () => (
    <div className="flex flex-col items-center justify-center py-8">
      <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
      <h3 className="text-lg font-medium mb-2">Uploading Your Form</h3>
      <p className="text-muted-foreground text-center max-w-md mb-4">
        Securely uploading your PDF to your form library...
      </p>
      <div className="w-full max-w-xs">
        <Progress value={uploadProgress} className="h-2" />
        <p className="text-xs text-muted-foreground text-center mt-2">
          {uploadProgress}% complete
        </p>
      </div>
    </div>
  );

  // Render analyzing step
  const renderAnalyzingStep = () => (
    <div className="flex flex-col items-center justify-center py-8">
      <div className="relative">
        <Sparkles className="h-12 w-12 text-primary mb-4 animate-pulse" />
        <div className="absolute inset-0 h-12 w-12 border-2 border-primary/30 rounded-full animate-ping" />
      </div>
      <h3 className="text-lg font-medium mb-2 mt-2">AI is Analyzing Your Form</h3>
      <p className="text-muted-foreground text-center max-w-md">
        Our AI is identifying form fields and mapping them to your job data.
        This usually takes 30-60 seconds.
      </p>
      <div className="mt-6 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <span>PDF uploaded successfully</span>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Extracting form fields...</span>
        </div>
      </div>
    </div>
  );

  // Render complete step
  const renderCompleteStep = () => (
    <div className="flex flex-col items-center justify-center py-8">
      <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
        <CheckCircle2 className="h-8 w-8 text-green-600" />
      </div>
      <h3 className="text-lg font-medium mb-2">Form Added Successfully!</h3>
      <p className="text-muted-foreground text-center max-w-md mb-6">
        Your form has been uploaded and analyzed.
        Review the field mappings to ensure accurate auto-fill.
      </p>
      <Alert className="bg-amber-50 border-amber-200 max-w-md">
        <Sparkles className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          <strong>Recommended:</strong> Review the AI-detected field mappings
          to ensure accurate auto-fill for your job data.
        </AlertDescription>
      </Alert>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Add Custom Form
          </DialogTitle>
          <DialogDescription>
            {step === 'select' && 'Upload a PDF form to add it to your personal form library.'}
            {step === 'details' && 'Provide some details about this form.'}
            {step === 'uploading' && 'Uploading your form...'}
            {step === 'analyzing' && 'Analyzing form structure...'}
            {step === 'complete' && 'Your form is ready to use!'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {step === 'select' && renderSelectStep()}
          {step === 'details' && renderDetailsStep()}
          {step === 'uploading' && renderUploadingStep()}
          {step === 'analyzing' && renderAnalyzingStep()}
          {step === 'complete' && renderCompleteStep()}

          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          {step === 'select' && (
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
          )}

          {step === 'details' && (
            <>
              <Button variant="outline" onClick={() => setStep('select')}>
                Back
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!formName.trim() || uploadMutation.isPending}
              >
                Upload & Analyze
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </>
          )}

          {step === 'complete' && (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Done
              </Button>
              <Button onClick={handleComplete}>
                Review Field Mappings
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CustomFormUpload;
